import { useMemo, useState } from "react";
import { ChevronLeft, Plus, Search } from "lucide-react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { BarraDeGuardado, CabeceraDelMantenedor } from "@/components/plantillas/CabeceraDelMantenedor";
import { ArbolDeLaPlantilla } from "@/components/plantillas/ArbolDeLaPlantilla";
import { EditarDatosDialog } from "@/components/plantillas/DialogosDePlantilla";
import {
  AgregarDelMaestroDialog,
  MoverDialog,
  NuevoBloqueDialog,
  QuitarItemDialog,
  type NuevoBloque,
  type QueMover,
} from "@/components/plantillas/DialogosDeEstructura";
import { DrawerDeItemDeLaPlantilla } from "@/components/plantillas/DrawerDeItemDeLaPlantilla";
import { PuntoDeObligatoriedad } from "@/components/plantillas/etiquetas";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { etiquetaDelRecurso } from "@/lib/produccionDelItem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCambiarUsoEnOtraPlantilla,
  useGuardarEstructura,
  useMaestroItems,
  usePlantilla,
  usePlantillas,
} from "@/hooks/usePlantillas";
import { useGuardiaDeCambios } from "@/hooks/useGuardiaDeCambios";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import {
  agregarDelMaestro,
  agregarItemLocal,
  borradorDesdeElApi,
  cambiarCondicion,
  cambiarObligatoriedad,
  editarItem,
  hayCambios as borradorCambio,
  localesSinPasarAlMaestro,
  pasarAlMaestro,
  payloadDeLaEstructura,
  quitarItemDelBorrador,
  recursosRepetidos,
  type Borrador,
} from "@/lib/borradorDePlantilla";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import {
  arbolDeLaEstructura,
  conteoPorObligatoriedad,
  crearGrupo,
  crearSubgrupo,
  eliminarVacio,
  existeGrupo,
  existeSubgrupo,
  moverGrupoUnPaso,
  moverItemAUbicacion,
  moverItemUnPaso,
  moverSubgrupoAGrupo,
  moverSubgrupoUnPaso,
  siguienteObligatoriedad,
  soltar,
  type Fila,
  type Obligatoriedad,
} from "@/lib/estructuraDePlantilla";
import { siguienteUso } from "@/lib/borradorDelMaestro";
import { cn } from "@/lib/utils";

type FiltroDeObligatoriedad = "todos" | Obligatoriedad;

function claveTemporal(): string {
  const aleatorio =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `tmp:${aleatorio}`;
}

const claveDeSubgrupo = (g: string, s: string | null) => `${g}|${s ?? ""}`;

/** Primer mensaje de un 422 (cualquier campo) o, si no, el del servidor. */
function mensajeDeGuardado(error: unknown): string {
  const errores = parseValidationErrors(error);
  if (errores) return Object.values(errores)[0];
  return getApiErrorMessage(error, "No pudimos guardar la plantilla. Inténtalo de nuevo.");
}

/**
 * Editor de una plantilla: su árbol Grupo › Subgrupo › Ítem.
 *
 * TRABAJA SOBRE UN BORRADOR. Cada gesto (reordenar, cambiar la obligatoriedad,
 * crear un ítem) modifica una copia en memoria, y "Guardar cambios" manda la
 * estructura entera en un solo PUT. Un guardado por gesto dejaría la
 * plantilla a medias si uno falla, y "Descartar" no tendría a qué volver.
 *
 * El borrador es `null` mientras nadie edita: ahí se lee directamente la
 * respuesta del API. Así no hay un efecto que copie una cosa a la otra, ni una
 * revalidación en segundo plano que pise lo que alguien está escribiendo.
 */
export function PlantillaEditar() {
  const { id: idDeLaRuta } = useParams();
  const id = Number(idDeLaRuta);

  const consulta = usePlantilla(id);
  const maestro = useMaestroItems();
  const plantillas = usePlantillas();
  const guardar = useGuardarEstructura(id);
  const cambiarUso = useCambiarUsoEnOtraPlantilla();
  const estado = estadoDeConsulta(consulta);

  const original = useMemo(
    () => (consulta.data ? borradorDesdeElApi(consulta.data) : null),
    [consulta.data],
  );
  const [edicion, setEdicion] = useState<Borrador | null>(null);
  const borrador = edicion ?? original;
  const hayCambios = Boolean(original && edicion && borradorCambio(original, edicion));

  const [errorDeGuardado, setErrorDeGuardado] = useState<string | null>(null);

  /* Estado de la vista (nada de esto es parte de la plantilla). */
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<FiltroDeObligatoriedad>("todos");
  const [soloLocales, setSoloLocales] = useState(false);
  const [gruposPlegados, setGruposPlegados] = useState<Set<string>>(new Set());
  const [subgruposPlegados, setSubgruposPlegados] = useState<Set<string>>(new Set());
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  /* Diálogos. */
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [agregando, setAgregando] = useState(false);
  const [nuevoBloque, setNuevoBloque] = useState<NuevoBloque | null>(null);
  const [moviendo, setMoviendo] = useState<QueMover | null>(null);
  const [porQuitar, setPorQuitar] = useState<string | null>(null);

  const guardia = useGuardiaDeCambios(hayCambios);

  const maestroItems = useMemo(() => maestro.data ?? [], [maestro.data]);

  const filas = borrador?.filas ?? [];
  const repetidos = borrador ? recursosRepetidos(borrador) : [];
  const items = borrador?.items ?? {};

  const q = busqueda.trim().toLowerCase();
  const filtrando = q !== "" || filtro !== "todos" || soloLocales;

  const arbol = useMemo(
    () =>
      arbolDeLaEstructura(filas, (fila) => {
        const item = fila.item ? items[fila.item] : null;
        if (!item) return false;
        if (filtro !== "todos" && fila.obligatoriedad !== filtro) return false;
        if (soloLocales && !(item.local && !item.publicar)) return false;
        if (q && !`${item.nombre} ${item.fuente} ${item.descripcion}`.toLowerCase().includes(q)) {
          return false;
        }
        return true;
      }),
    [filas, items, filtro, soloLocales, q],
  );
  const arbolCompleto = useMemo(() => arbolDeLaEstructura(filas, () => true), [filas]);

  const conteo = conteoPorObligatoriedad(filas);
  const pendientesDeMaestro = borrador ? localesSinPasarAlMaestro(borrador) : [];

  const gruposDelMaestro = useMemo(() => new Set(maestroItems.map((i) => i.grupo)), [maestroItems]);
  const subgruposDelMaestro = useMemo(
    () => new Set(maestroItems.map((i) => `${i.grupo}|${i.subgrupo ?? ""}`)),
    [maestroItems],
  );
  const fuentes = useMemo(
    () => [...new Set(maestroItems.map((i) => i.fuente).filter((f): f is string => Boolean(f)))],
    [maestroItems],
  );

  const refsEnLaPlantilla = new Set(filas.map((f) => f.item).filter((r): r is string => r !== null));
  const disponibles = maestroItems.filter((i) => !refsEnLaPlantilla.has(String(i.id)));

  const nombreDePlantilla = (plantillaId: number) =>
    plantillas.data?.find((p) => p.id === plantillaId)?.nombre ?? `Plantilla ${plantillaId}`;

  /**
   * Las OTRAS plantillas frente a un ítem compartido, según el maestro: las que
   * lo usan con su obligatoriedad y las que no, como "No incluido" — desde el
   * cajón también se puede sumarlo a una de ellas.
   */
  const usoEnOtras = (ref: string) => {
    const item = maestroItems.find((i) => String(i.id) === ref);
    return (plantillas.data ?? [])
      .filter((p) => p.id !== id)
      .map((p) => ({
        plantillaId: p.id,
        nombre: p.nombre,
        obligatoriedad: (item?.uso[String(p.id)] ?? "") as Obligatoriedad | "",
      }));
  };

  /**
   * Un clic en el estado de una OTRA plantilla guarda en el acto: es un cambio
   * del maestro, no del borrador de esta plantilla (que ni se toca).
   */
  const cambiarUsoEnOtra = (ref: string, plantillaId: number, actual: Obligatoriedad | "") => {
    const item = maestroItems.find((i) => String(i.id) === ref);
    if (!item) return;
    const siguiente = siguienteUso(actual);
    const nombre = nombreDePlantilla(plantillaId);
    cambiarUso.mutate(
      {
        id: item.id,
        grupo: item.grupo,
        subgrupo: item.subgrupo,
        nombre: item.nombre,
        descripcion: item.descripcion,
        tipo_contenido: item.tipo_contenido,
        fuente: item.fuente,
        condicion_sugerida: item.condicion_sugerida,
        uso: { [String(plantillaId)]: siguiente === "" ? null : siguiente },
      },
      {
        onSuccess: () =>
          toast(
            siguiente === ""
              ? `“${item.nombre}” ya no está en ${nombre}`
              : `“${item.nombre}” quedó como ${siguiente} en ${nombre}`,
          ),
        onError: (e) =>
          toast.error(
            getApiErrorMessage(e, "No pudimos cambiarlo en esa plantilla. Inténtalo de nuevo."),
          ),
      },
    );
  };

  /* --- Edición ----------------------------------------------------------- */

  const cambiar = (siguiente: Borrador) => setEdicion(siguiente);
  const cambiarFilas = (siguiente: Fila[] | null) => {
    if (borrador && siguiente) cambiar({ ...borrador, filas: siguiente });
  };

  const filaDe = (ref: string) => filas.find((f) => f.item === ref) ?? null;

  const alternar = (conjunto: Set<string>, clave: string, poner: (s: Set<string>) => void) => {
    const siguiente = new Set(conjunto);
    if (siguiente.has(clave)) siguiente.delete(clave);
    else siguiente.add(clave);
    poner(siguiente);
  };

  const nuevoItem = (grupo: string, subgrupo: string | null) => {
    if (!borrador) return;
    const ref = claveTemporal();
    cambiar(agregarItemLocal(borrador, grupo, subgrupo, ref));
    setGruposPlegados((s) => {
      const siguiente = new Set(s);
      siguiente.delete(grupo);
      return siguiente;
    });
    setSubgruposPlegados((s) => {
      const siguiente = new Set(s);
      siguiente.delete(claveDeSubgrupo(grupo, subgrupo));
      return siguiente;
    });
    setSeleccionado(ref);
  };

  const quitar = (ref: string) => {
    if (!borrador) return;
    const item = borrador.items[ref];
    cambiar(quitarItemDelBorrador(borrador, ref));
    if (seleccionado === ref) setSeleccionado(null);
    setPorQuitar(null);
    toast(item?.local ? `“${item.nombre}” eliminado` : `“${item?.nombre}” quitado de la plantilla`);
  };

  const crearBloque = (bloque: NuevoBloque, nombre: string) => {
    if (bloque.tipo === "grupo") cambiarFilas(crearGrupo(filas, nombre));
    else cambiarFilas(crearSubgrupo(filas, bloque.grupo, nombre));
    setBusqueda("");
    setFiltro("todos");
    setSoloLocales(false);
    setNuevoBloque(null);
  };

  const pasarTodosAlMaestro = () => {
    if (!borrador) return;
    cambiar(pasarAlMaestro(borrador, pendientesDeMaestro));
    setSoloLocales(false);
    toast("Pasarán al maestro cuando guardes los cambios");
  };

  const guardarCambios = (alTerminar?: () => void) => {
    if (!borrador) return;
    setErrorDeGuardado(null);
    const publicados = Object.values(borrador.items).filter((i) => i.local && i.publicar).length;
    guardar.mutate(payloadDeLaEstructura(borrador), {
      onSuccess: () => {
        setEdicion(null);
        toast.success(
          publicados > 0
            ? `Cambios guardados. ${publicados} ítem${publicados > 1 ? "s pasaron" : " pasó"} al maestro.`
            : "Cambios guardados",
        );
        alTerminar?.();
      },
      onError: (e) => setErrorDeGuardado(mensajeDeGuardado(e)),
    });
  };

  const descartar = () => {
    setEdicion(null);
    setSeleccionado(null);
    setErrorDeGuardado(null);
    toast("Cambios descartados");
  };

  /* --- Vista --------------------------------------------------------------- */

  const subgruposConNombre = arbolCompleto.flatMap((g) =>
    g.subgrupos.filter((s) => s.subgrupo !== null).map((s) => claveDeSubgrupo(g.grupo, s.subgrupo)),
  );
  const hayGrupoAbierto = arbolCompleto.some((g) => !gruposPlegados.has(g.grupo));
  const haySubgrupoAbierto = subgruposConNombre.some((k) => !subgruposPlegados.has(k));

  const itemSeleccionado = seleccionado && borrador ? (borrador.items[seleccionado] ?? null) : null;
  const filaSeleccionada = seleccionado ? filaDe(seleccionado) : null;
  const itemPorQuitar = porQuitar && borrador ? (borrador.items[porQuitar] ?? null) : null;

  const chips: { clave: FiltroDeObligatoriedad; etiqueta: string; cantidad: number }[] = [
    { clave: "todos", etiqueta: "Todos", cantidad: conteo.total },
    { clave: "obligatorio", etiqueta: "Obligatorios", cantidad: conteo.obligatorios },
    { clave: "condicional", etiqueta: "Condicionales", cantidad: conteo.condicionales },
    { clave: "opcional", etiqueta: "Opcionales", cantidad: conteo.opcionales },
  ];

  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <CabeceraDelMantenedor vista="plantillas">
        {borrador ? (
          <BarraDeGuardado
            hayCambios={hayCambios}
            guardando={guardar.isPending}
            onDescartar={descartar}
            onGuardar={() => guardarCambios()}
          />
        ) : null}
      </CabeceraDelMantenedor>

      <Link
        to="/admin/contenidos"
        className="text-ink-muted hover:text-ink mb-3.5 inline-flex items-center gap-1.5 text-[0.8125rem] font-bold"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        Plantillas
      </Link>

      <div className="space-y-5">
      {estado.sinConexion && <EstadoSinConexion que="la plantilla" />}

      {estado.hayError && (
        <EstadoDeError
          titulo="No se pudo cargar la plantilla"
          error={consulta.error}
          onReintentar={consulta.refetch}
          reintentando={consulta.isFetching}
        />
      )}

      {estado.cargando && (
        <div className="space-y-3">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-80 w-full rounded-xl" />
        </div>
      )}

      {errorDeGuardado ? (
        <Alert variant="destructive">
          <AlertDescription>{errorDeGuardado}</AlertDescription>
        </Alert>
      ) : null}

      {consulta.data && borrador ? (
        <>
          {repetidos.length > 0 ? (
            <Alert data-testid="aviso-recursos-repetidos">
              <AlertDescription>
                <p className="font-bold">Hay contenido que se repetiría en el documento</p>
                <p className="mt-1">
                  Cada recurso se imprime una sola vez: en el primer ítem que lo usa. Los demás
                  solo remiten a esa sección.
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {repetidos.map((r) => (
                    <li key={r.recurso}>
                      <span className="font-semibold">{etiquetaDelRecurso(r.recurso)}</span>:{" "}
                      {r.items.join(", ")}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
            <div className="min-w-[min(100%,22.5rem)] flex-1">
              <h2 className="text-ink text-h2 leading-tight">{consulta.data.plantilla.nombre}</h2>
              {consulta.data.plantilla.proposito ? (
                <p className="text-ink-body mt-2 max-w-[45rem] text-sm leading-[1.55] font-medium text-pretty">
                  {consulta.data.plantilla.proposito}
                </p>
              ) : null}
              {consulta.data.plantilla.formatos_por_defecto?.length ? (
                <p className="text-ink-muted mt-3 text-xs font-semibold">
                  Formato por defecto:{" "}
                  <span className="text-ink-body">
                    {consulta.data.plantilla.formatos_por_defecto.map((f) => f.nombre).join(" / ")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="flex flex-none flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditandoDatos(true)}>
                Editar datos
              </Button>
              <Button variant="outline" onClick={() => setNuevoBloque({ tipo: "grupo" })}>
                <Plus className="size-4" />
                Nuevo grupo
              </Button>
              <Button variant="secondary" onClick={() => setAgregando(true)}>
                <Plus className="size-4" />
                Agregar del maestro
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 pt-2">
            <div className="relative min-w-[12.5rem] max-w-[21.25rem] flex-1">
              <Search
                aria-hidden="true"
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
              />
              <Input
                aria-label="Buscar en la plantilla"
                placeholder="Buscar ítem o fuente…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {chips.map((chip) => (
                <Button
                  key={chip.clave}
                  type="button"
                  size="sm"
                  variant={filtro === chip.clave ? "secondary" : "ghost"}
                  aria-pressed={filtro === chip.clave}
                  onClick={() => setFiltro(chip.clave)}
                  className="h-7 rounded-full px-3"
                >
                  {chip.clave !== "todos" ? (
                    <PuntoDeObligatoriedad obligatoriedad={chip.clave} />
                  ) : null}
                  {chip.etiqueta}
                  <span className="font-mono text-[0.65625rem] opacity-70">{chip.cantidad}</span>
                </Button>
              ))}
            </div>

            <span className="text-ink-hint ml-auto text-xs font-semibold">
              {filtrando
                ? "Quita los filtros para reordenar"
                : "Arrastra o usa las flechas para reordenar"}
            </span>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-ink-muted hover:text-ink h-7 px-1 text-[0.78rem]"
              disabled={subgruposConNombre.length === 0}
              onClick={() => {
                setSubgruposPlegados(haySubgrupoAbierto ? new Set(subgruposConNombre) : new Set());
                setGruposPlegados(new Set());
              }}
            >
              {haySubgrupoAbierto ? "Colapsar subgrupos" : "Expandir subgrupos"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-ink-muted hover:text-ink h-7 px-1 text-[0.78rem]"
              onClick={() => {
                setGruposPlegados(
                  hayGrupoAbierto ? new Set(arbolCompleto.map((g) => g.grupo)) : new Set(),
                );
                setSubgruposPlegados(new Set());
              }}
            >
              {hayGrupoAbierto ? "Colapsar todo" : "Expandir todo"}
            </Button>
          </div>

          {pendientesDeMaestro.length > 0 ? (
            <div className="bg-info-soft text-info-soft-foreground flex flex-wrap items-center gap-3 rounded-lg px-4 py-2.5 text-sm">
              <span className="font-semibold">
                {pendientesDeMaestro.length === 1
                  ? "1 ítem creado en esta plantilla aún no está en el maestro."
                  : `${pendientesDeMaestro.length} ítems creados en esta plantilla aún no están en el maestro.`}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSoloLocales((v) => !v)}
              >
                {soloLocales ? "Ver todos" : "Ver solo esos"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={pasarTodosAlMaestro}>
                Pasar todos al maestro
              </Button>
            </div>
          ) : null}

          {arbol.every((g) => g.visibles === 0 && filtrando) ? (
            <p className="text-ink-muted rounded-xl border p-6 text-center text-sm">
              Ningún ítem coincide con la búsqueda o el filtro.
            </p>
          ) : (
            <ArbolDeLaPlantilla
              arbol={arbol}
              items={items}
              filtrando={filtrando}
              grupoEnElMaestro={(g) => gruposDelMaestro.has(g)}
              subgrupoEnElMaestro={(g, s) => subgruposDelMaestro.has(`${g}|${s}`)}
              grupoColapsado={(g) => gruposPlegados.has(g)}
              subgrupoColapsado={(g, s) => subgruposPlegados.has(claveDeSubgrupo(g, s))}
              seleccionado={seleccionado}
              acciones={{
                seleccionar: setSeleccionado,
                ciclar: (ref) => {
                  const fila = filaDe(ref);
                  if (borrador && fila?.obligatoriedad) {
                    cambiar(
                      cambiarObligatoriedad(borrador, ref, siguienteObligatoriedad(fila.obligatoriedad)),
                    );
                  }
                },
                moverItem: (ref, dir) => cambiarFilas(moverItemUnPaso(filas, ref, dir)),
                pedirMoverItem: (ref) => {
                  const fila = filaDe(ref);
                  if (fila) {
                    setMoviendo({
                      tipo: "item",
                      ref,
                      nombre: items[ref]?.nombre ?? "",
                      grupo: fila.grupo,
                      subgrupo: fila.subgrupo,
                    });
                  }
                },
                pedirQuitar: setPorQuitar,
                nuevoItem,
                nuevoSubgrupo: (grupo) => setNuevoBloque({ tipo: "subgrupo", grupo }),
                moverGrupo: (grupo, dir) => cambiarFilas(moverGrupoUnPaso(filas, grupo, dir)),
                moverSubgrupo: (grupo, subgrupo, dir) => {
                  const resultado = moverSubgrupoUnPaso(filas, grupo, subgrupo, dir);
                  if (!resultado) return;
                  cambiarFilas(resultado.filas);
                  if (resultado.grupoDestino) toast(`Subgrupo movido a ${resultado.grupoDestino}`);
                },
                pedirMoverSubgrupo: (grupo, subgrupo) =>
                  setMoviendo({ tipo: "subgrupo", grupo, subgrupo }),
                eliminarVacio: (grupo, subgrupo) => cambiarFilas(eliminarVacio(filas, grupo, subgrupo)),
                alternarGrupo: (g) => alternar(gruposPlegados, g, setGruposPlegados),
                alternarSubgrupo: (g, s) =>
                  alternar(subgruposPlegados, claveDeSubgrupo(g, s), setSubgruposPlegados),
                soltar: (origen, destino) => cambiarFilas(soltar(filas, origen, destino)),
              }}
            />
          )}

          <p className={cn("text-ink-hint text-xs", filas.length === 0 && "hidden")}>
            {conteo.total} ítems · {conteo.obligatorios} obligatorios · {conteo.condicionales}{" "}
            condicionales · {conteo.opcionales} opcionales
          </p>

          <EditarDatosDialog
            abierto={editandoDatos}
            plantilla={consulta.data.plantilla}
            onCerrar={() => setEditandoDatos(false)}
          />

          <AgregarDelMaestroDialog
            abierto={agregando}
            disponibles={disponibles}
            onCerrar={() => setAgregando(false)}
            onAgregar={(elegidos, como) => {
              cambiar(agregarDelMaestro(borrador, elegidos, como));
              setAgregando(false);
              toast(
                `${elegidos.length} ítem${elegidos.length > 1 ? "s agregados" : " agregado"}`,
              );
            }}
          />

          <NuevoBloqueDialog
            bloque={nuevoBloque}
            existeGrupo={(nombre) => existeGrupo(filas, nombre)}
            existeSubgrupo={(grupo, nombre) => existeSubgrupo(filas, grupo, nombre)}
            onCrear={crearBloque}
            onCerrar={() => setNuevoBloque(null)}
          />

          <MoverDialog
            que={moviendo}
            arbol={arbolCompleto}
            onMoverItem={(ref, grupo, subgrupo) => {
              cambiarFilas(moverItemAUbicacion(filas, ref, grupo, subgrupo));
              setMoviendo(null);
            }}
            onMoverSubgrupo={(grupo, subgrupo, destino) => {
              cambiarFilas(moverSubgrupoAGrupo(filas, grupo, subgrupo, destino));
              setMoviendo(null);
            }}
            onCerrar={() => setMoviendo(null)}
          />

          <QuitarItemDialog
            item={itemPorQuitar}
            enOtrasPlantillas={porQuitar ? usoEnOtras(porQuitar).filter((u) => u.obligatoriedad).length : 0}
            onConfirmar={() => porQuitar && quitar(porQuitar)}
            onCancelar={() => setPorQuitar(null)}
          />

          <DrawerDeItemDeLaPlantilla
            item={itemSeleccionado}
            fila={filaSeleccionada}
            otrasPlantillas={seleccionado ? usoEnOtras(seleccionado) : []}
            onCambiarUsoEnOtra={(plantillaId, actual) =>
              seleccionado && cambiarUsoEnOtra(seleccionado, plantillaId, actual)
            }
            fuentesConocidas={fuentes}
            onCerrar={() => setSeleccionado(null)}
            onEditar={(cambios) => seleccionado && cambiar(editarItem(borrador, seleccionado, cambios))}
            onObligatoriedad={(valor) =>
              seleccionado && cambiar(cambiarObligatoriedad(borrador, seleccionado, valor))
            }
            onCondicion={(valor) =>
              seleccionado && cambiar(cambiarCondicion(borrador, seleccionado, valor))
            }
            onPasarAlMaestro={() => {
              if (!seleccionado) return;
              cambiar(pasarAlMaestro(borrador, [seleccionado]));
              toast("Pasará al maestro cuando guardes los cambios");
            }}
            onQuitar={() => seleccionado && setPorQuitar(seleccionado)}
          />
        </>
      ) : null}

      </div>

      <DialogoCambiosSinGuardar
        abierto={guardia.bloqueado}
        descripcion="Esta plantilla tiene cambios que no se han guardado. Si sales ahora se perderán y quedará como en el último guardado."
        guardando={guardar.isPending}
        onGuardarYSalir={() =>
          guardarCambios(() => {
            guardia.desactivar();
            guardia.continuar();
          })
        }
        onSalirSinGuardar={guardia.continuar}
        onCancelar={guardia.cancelar}
      />
    </div>
  );
}
